/* ––
 * ––– Imports
 * –––––––––––––––––––––––––––––––– */
import { Map } from 'immutable';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useTheme, lighten, darken } from '@mui/material/styles';

/* ––
 * ––– Constants
 * –––––––––––––––––––––––––––––––– */
const DEFAULT_SLOT_DATE_FORMAT_OPTIONS = {
  weekday: 'long',
  month:   'long',
  day:     'numeric',
  year:    'numeric',
};

/* ––
 * ––– Inner Components
 * –––––––––––––––––––––––––––––––– */
function VerificationButton({
  action, loading, onClick, children, disabled,
}) {
  /* –– Resources
   * –––––––––––––––––––––––––––––––– */
  const theme = useTheme();

  /* –– Render
   * –––––––––––––––––––––––––––––––– */
  return (
    <ButtonBase
      onClick={onClick}
      disableRipple
      disabled={disabled}
      data-testid={`appointment-slot-${action}-button`}
      sx={{
        fontFamily:      theme.typography.fontFamily,
        fontWeight:      theme.typography.fontWeightMedium,
        textAlign:       'center',
        padding:         2,
        width:           action === 'confirm' ? '60%' : 'auto',
        borderRadius:    1.5,
        borderWidth:     1.5,
        borderStyle:     'solid',
        borderColor:     action === 'confirm' ? 'primary.main' : 'grey.300',
        backgroundColor: action === 'confirm' ? lighten(theme.palette.primary.main, 0.9) : 'transparent',
        transition:      theme.transitions.create(
          ['background-color', 'border-color', 'color'],
          {
            duration: theme.transitions.duration.short,
            easing:   theme.transitions.easing.easeOut,
          },
        ),

        '&:hover': {
          backgroundColor: action === 'confirm' ? lighten(theme.palette.primary.main, 0.8) : 'background.dark',
        },
        '&:focus': {
          backgroundColor: action === 'confirm' ? lighten(theme.palette.primary.main, 0.8) : 'background.dark',
        },
        '&:active': {
          backgroundColor: action === 'confirm' ? 'primary.main' : darken(theme.palette.background.dark, 0.1),
          borderColor:     action === 'confirm' ? 'primary.main' : darken(theme.palette.background.dark, 0.1),
          color:           action === 'confirm' ? 'primary.contrastText' : 'text.primary',
        },
        '&:disabled': {
          opacity: 0.5,
          color:   'text.disabled',
        },
      }}
    >
      {loading ? <CircularProgress size={20} color={action === 'confirm' ? 'primary' : 'inherit'} /> : children}
    </ButtonBase>
  );
}

VerificationButton.propTypes = {
  action:   PropTypes.string.isRequired,
  loading:  PropTypes.bool.isRequired,
  onClick:  PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  disabled: PropTypes.bool.isRequired,
};

/* ––
 * ––– Component
 * –––––––––––––––––––––––––––––––– */
function AppointmentSlotVerifier({
  slot, loading, onConfirm, onCancel, disabled, error,
}) {
  /* –– Render
   * –––––––––––––––––––––––––––––––– */
  if (!Map.isMap(slot)) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" textAlign="center" paddingY={4}>
        <Typography variant="h5" textAlign="center" fontWeight="medium">{t('eos.landing_page.text.booking.no_slot_selected')}</Typography>
      </Box>
    );
  }

  const formattedSlotTimeframe = useMemo(
    () => {
      const startTime = slot.get('startTime');
      const endTime = slot.get('endTime');

      const formattedDate = startTime.toLocaleString(DEFAULT_SLOT_DATE_FORMAT_OPTIONS);
      const formattedStartTime = startTime.toLocaleString(DateTime.TIME_SIMPLE);
      const formattedEndTime = endTime.toLocaleString(DateTime.TIME_SIMPLE);
      return `${formattedDate} ${formattedStartTime} - ${formattedEndTime} (${startTime.offsetNameShort})`;
    },
    [slot],
  );

  return (
    <Box pt={{ xs: 2, md: 2.5 }}>
      <Typography variant="h3" textAlign="center" fontWeight="medium">{formattedSlotTimeframe}</Typography>

      {error && (
        <Box
          display="flex"
          sx={{
            mt: 1.5, px: 1.5, py: 1, backgroundColor: 'error.light', borderRadius: 1,
          }}
        >
          <ErrorOutlineIcon color="error" sx={{ mr: 1, mt: 0.15 }} fontSize="small" />
          <Typography variant="body" color="error" sx={{ lineHeight: 1.4 }}>
            {t('eos.landing_page.text.booking.appointment_creation_error')}
          </Typography>
        </Box>
      )}

      <Box display="flex" flexDirection="column" alignItems="center" gap={2} paddingY={2}>
        <VerificationButton action="confirm" loading={loading} onClick={onConfirm} disabled={disabled}>
          <Typography variant="body" fontWeight="medium">{t('eos.landing_page.label.booking.confirm')}</Typography>
        </VerificationButton>
        <VerificationButton action="cancel" loading={false} onClick={onCancel} disabled={disabled}>
          <Typography variant="body" fontWeight="medium">{t('eos.landing_page.label.booking.cancel')}</Typography>
        </VerificationButton>
      </Box>
    </Box>
  );
}

/* ––
 * ––– Props declaration
 * –––––––––––––––––––––––––––––––– */
AppointmentSlotVerifier.propTypes = {
  slot:      PropTypes.oneOfType([PropTypes.instanceOf(Map), PropTypes.oneOf([null])]),
  loading:   PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel:  PropTypes.func.isRequired,
  disabled:  PropTypes.bool.isRequired,
  error:     PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
};

/* ––
 * ––– Exports
 * –––––––––––––––––––––––––––––––– */
export default AppointmentSlotVerifier;
